
import React, { useState, useEffect } from 'react';
import { AppStep, ExtractedData } from './types';
import { extractDataFromPdfs } from './services/extractionService';
import { saveExtractionRecord, logoutUser, auth } from './services/firebaseService';
import { onAuthStateChanged, User } from 'firebase/auth';
import { FileUpload } from './components/FileUpload';
import { DataReview } from './components/DataReview';
import { AsanaCreator } from './components/AsanaCreator';
import { ExternalFormHelper } from './components/ExternalFormHelper';
import { TaskSettings } from './components/TaskSettings';
import { ExtensionDownload } from './components/ExtensionDownload';
import { AuthPage } from './components/AuthPage';
import { Sparkles, ArrowRight, LogOut, UserCircle } from 'lucide-react';

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [step, setStep] = useState<AppStep>(AppStep.UPLOAD);
  const [data, setData] = useState<ExtractedData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleFileProcess = async (base64Array: string[]) => {
    setIsProcessing(true);
    try {
      const result = await extractDataFromPdfs(base64Array);
      console.log("Extracted Data:", result);
      
      const fullData = { ...result, pdfData: base64Array };
      setData(fullData);
      
      // Auto-save to Firebase history record - OMITTING pdfData to avoid Firestore limits
      const { pdfData, ...saveableData } = fullData;
      await saveExtractionRecord(saveableData);
      
      setStep(AppStep.REVIEW);
    } catch (error: any) {
      console.error("Extraction error:", error);
      alert(`Failed to process PDFs: ${error.message || "Unknown error"}. Ensure your OPENROUTER_API_KEY is correctly set in your Cloudflare Pages/Environment variables.`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLogout = async () => {
    await logoutUser();
  };

  const handleDataUpdate = (updatedData: ExtractedData) => {
    setData(updatedData);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 to-purple-100">
        <div className="flex flex-col items-center gap-4 glass-panel p-8 rounded-2xl shadow-xl">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-indigo-900 font-medium">Loading MyESALON Order Automation...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 glass-panel border-b border-white/30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-xl shadow-lg shadow-blue-500/20">
                    <Sparkles className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700 tracking-tight">
                    MyESALON Order Automation
                </h1>
            </div>
            
            <div className="hidden md:flex items-center gap-2 text-sm bg-white/40 p-1.5 rounded-full border border-white/40 shadow-inner">
                <StepIndicator current={step} target={AppStep.UPLOAD} label="1. Upload" />
                <ArrowRight className="w-3 h-3 text-slate-400" />
                <StepIndicator current={step} target={AppStep.REVIEW} label="2. Review" />
                <ArrowRight className="w-3 h-3 text-slate-400" />
                <StepIndicator current={step} target={AppStep.ACTIONS} label="3. Execute" />
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600 bg-white/50 border border-white/40 px-3 py-1.5 rounded-full shadow-sm">
                <UserCircle className="w-4 h-4 text-indigo-500" />
                <span className="truncate max-w-[150px] font-medium">{user.email}</span>
              </div>
              <button 
                onClick={handleLogout}
                className="text-slate-500 hover:text-red-600 p-2 rounded-xl hover:bg-red-50/50 transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
        </div>
      </header>

      <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        {step === AppStep.UPLOAD && (
            <div className="animate-in fade-in zoom-in duration-500">
                <div className="text-center mb-12">
                    <h2 className="text-5xl font-extrabold text-slate-800 mb-4 tracking-tight drop-shadow-sm">
                        Automate Your Onboarding
                    </h2>
                    <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8 font-light">
                        Upload business PDFs to extract info, create Asana projects, and pre-fill signup forms via cloud-synced automation.
                    </p>
                    
                    <FileUpload onFileProcess={handleFileProcess} isProcessing={isProcessing} />

                    <div className="flex flex-col gap-4 mt-12 max-w-2xl mx-auto">
                      <TaskSettings />
                      <ExtensionDownload />
                    </div>
                </div>
            </div>
        )}

        {step === AppStep.REVIEW && data && (
            <DataReview 
                data={data} 
                onEdit={handleDataUpdate} 
                onConfirm={() => setStep(AppStep.ACTIONS)} 
                onRetake={() => {
                    setStep(AppStep.UPLOAD);
                    setData(null);
                }}
            />
        )}

        {step === AppStep.ACTIONS && data && (
            <div className="max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="mb-8 flex items-center justify-between glass-panel p-6 rounded-3xl border-white/60 shadow-lg">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800">Workflow Actions</h2>
                        <p className="text-slate-500 font-medium text-sm">Execute tasks using the corrected data from review.</p>
                    </div>
                    <button 
                        onClick={() => setStep(AppStep.REVIEW)}
                        className="text-slate-500 hover:text-indigo-600 text-sm font-bold px-6 py-2.5 bg-white/40 hover:bg-white/80 rounded-xl transition-all border border-white/60 shadow-sm"
                    >
                        &larr; Back to Review
                    </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                    <AsanaCreator data={data} />
                    <ExternalFormHelper data={data} />
                </div>
            </div>
        )}
      </main>
    </div>
  );
}

const StepIndicator: React.FC<{ current: AppStep; target: AppStep; label: string }> = ({ current, target, label }) => {
    const steps = [AppStep.UPLOAD, AppStep.REVIEW, AppStep.ACTIONS];
    const currentIndex = steps.indexOf(current);
    const targetIndex = steps.indexOf(target);
    const isActive = current === target;
    const isCompleted = currentIndex > targetIndex;

    return (
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full transition-all duration-300 ${
            isActive ? 'bg-white shadow-sm text-indigo-700 font-bold transform scale-105' : 
            isCompleted ? 'text-emerald-600 font-medium' : 'text-slate-400'
        }`}>
            {isCompleted && <span className="text-emerald-500">✓</span>}
            <span>{label}</span>
        </div>
    );
};
